import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { ThreeDots } from "react-loader-spinner";
import LoadingIcon from "../../../../components/Loading";
import TruckIconFO from "../../../../icons/TruckIconFO";
import RailIconFO from "../../../../icons/RailIconFO";
import { icons } from "../../../../svg";
import NitroService from "../../../../utils/nitro.service";
import NitroRequestService from "../../../../utils/nitroRequest.service";
import { Quote } from "../../../../types/IMultiLocationQuoteMobile";
import type { MissingRailFreightSectionItem } from "../../../../types/IMissingRailFreight";
import type IRouteValidationResult from "../../../competitiveAnalysisModal/components/RouteValidation/types/IRouteValidation";
import type { UsePublicTariffCheckParams } from "../../../../types/IRailRateRequest";
import RequestTariffSection from "../../manageRailRatePanel/RequestTariffSection";
import { usePublicTariffCheck } from "../../manageRailRatePanel/usePublicTariffCheck";
import {
  formatProductName,
  formatOriginNameDisplay,
  getFavoriteOriginType,
  NoGuidanceSectionItem,
} from "../../multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";
import "./multiLocQuoteMissingOrigin.css";

type MissingFreightTab = "TRUCK" | "RAIL";

type MissingMilesOrigin = {
  originId: string;
  originName: string;
  competitor: boolean;
  validation: IRouteValidationResult;
};

type MultiLocQuoteMissingOriginProps = {
  shouldShowTruckMissing: boolean;
  shouldShowRailMissing: boolean;
  missingFreightTab: string;
  setMissingFreightTab: (tab: MissingFreightTab) => void;
  originsMissingProductForActiveTab: Quote[];
  loadingAddProductByKey: Record<string, boolean>;
  onAddMissingProduct: (quote: Quote) => void;
  missingMilesOrigins: MissingMilesOrigin[];
  setMissingMilesOrigins: React.Dispatch<
    React.SetStateAction<MissingMilesOrigin[]>
  >;
  destinationName: string;
  loadingMissingMiles: boolean;
  originsWithNoGuidance: NoGuidanceSectionItem[];
  originsMissingRailFreightForDisplay: MissingRailFreightSectionItem[];
  requestedRailRateByOriginKey: Record<string, boolean>;
  failedRailRateRequestByOriginKey: Record<string, boolean>;
  loadingRailRateRequestByOriginKey: Record<string, boolean>;
  setLoadingRailRateRequestByOriginKey: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  tariffCheckParams: UsePublicTariffCheckParams;
  postCreateRailRequests: (
    origins: Array<{ originId: string; originName: string }>,
  ) => Promise<void>;
  postRequestForRailRate: (
    origins: MissingRailFreightSectionItem[],
  ) => Promise<void>;
  markRailRateRequested: (originKeys: string[]) => void;
  openSingleRailRateRequest: (origin: MissingRailFreightSectionItem) => void;
  savedNotification: (message: string) => void;
  failedNotification: (title: string, message: string) => void;
  onRequestMultipleRailRates: () => void;
  loadingRailMissingFreight: boolean;
};

const MultiLocQuoteMissingOrigin: React.FC<MultiLocQuoteMissingOriginProps> = ({
  shouldShowTruckMissing,
  shouldShowRailMissing,
  missingFreightTab,
  setMissingFreightTab,
  originsMissingProductForActiveTab,
  loadingAddProductByKey,
  onAddMissingProduct,
  missingMilesOrigins,
  setMissingMilesOrigins,
  destinationName,
  loadingMissingMiles,
  originsWithNoGuidance,
  originsMissingRailFreightForDisplay,
  requestedRailRateByOriginKey,
  failedRailRateRequestByOriginKey,
  loadingRailRateRequestByOriginKey,
  setLoadingRailRateRequestByOriginKey,
  tariffCheckParams,
  postCreateRailRequests,
  postRequestForRailRate,
  markRailRateRequested,
  openSingleRailRateRequest,
  savedNotification,
  failedNotification,
  onRequestMultipleRailRates,
  loadingRailMissingFreight,
}) => {
  const auth = useAuth();
  const [loadingMileageRequestByOrigin, setLoadingMileageRequestByOrigin] =
    useState<Record<string, boolean>>({});
  const autoStartedPublicTariffChecksRef = useRef<Set<string>>(new Set());
  const {
    publicTariffStatusByOriginKey,
    publicTariffRateByOriginKey,
    handleCheckPublicTariffForOrigin,
  } = usePublicTariffCheck(tariffCheckParams);

  const handleMileageRequest = async (
    missingMilesOrigin: MissingMilesOrigin,
  ) => {
    const originLoadingKey = `${
      missingMilesOrigin.competitor ? "competitor" : "koch"
    }-${missingMilesOrigin.originId}`;

    setLoadingMileageRequestByOrigin((prev) => ({
      ...prev,
      [originLoadingKey]: true,
    }));

    try {
      const marketer = `${auth.user?.profile?.given_name || ""} ${
        auth.user?.profile?.family_name || ""
      }`.trim();

      await NitroRequestService.postMileageRequest({
        marketer,
        product:
          tariffCheckParams.selectedProduct ||
          tariffCheckParams.product ||
          "AMMONIA",
        mode: "Truck",
        origin: missingMilesOrigin.originName,
        originZipCode: missingMilesOrigin.validation?.originZipCode ?? "",
        destination: destinationName || "",
        destinationZipCode:
          missingMilesOrigin.validation?.destinationZipCode ?? "",
        comments: "",
      });

      await NitroService.updateMileageRequestStatus({
        originZip: missingMilesOrigin.validation?.originZipCode ?? "",
        destinationZip: missingMilesOrigin.validation?.destinationZipCode ?? "",
        user: marketer,
      });

      setMissingMilesOrigins((prev) =>
        prev.map((origin) =>
          origin.originId === missingMilesOrigin.originId &&
          !!origin.competitor === !!missingMilesOrigin.competitor
            ? {
                ...origin,
                validation: { ...origin.validation, mileageRequested: true },
              }
            : origin,
        ),
      );

      savedNotification("Mileage request sent successfully");
    } catch (error: any) {
      failedNotification(
        "Failed to send mileage request",
        error?.message || "Unknown error",
      );
    } finally {
      setLoadingMileageRequestByOrigin((prev) => ({
        ...prev,
        [originLoadingKey]: false,
      }));
    }
  };

  useEffect(() => {
    const displayedOriginKeySet = new Set(
      originsMissingRailFreightForDisplay.map((origin) => origin.originKey),
    );
    Array.from(autoStartedPublicTariffChecksRef.current).forEach(
      (originKey) => {
        const status = publicTariffStatusByOriginKey[originKey];
        if (
          !displayedOriginKeySet.has(originKey) ||
          (status && status !== "checking")
        ) {
          autoStartedPublicTariffChecksRef.current.delete(originKey);
        }
      },
    );

    originsMissingRailFreightForDisplay.forEach((origin) => {
      const requested =
        requestedRailRateByOriginKey[origin.originKey] ||
        (origin.railValidation as any)?.railRateRequested === true;
      const status = publicTariffStatusByOriginKey[origin.originKey];
      if (
        requested &&
        (!status || status === "checking") &&
        !autoStartedPublicTariffChecksRef.current.has(origin.originKey)
      ) {
        autoStartedPublicTariffChecksRef.current.add(origin.originKey);
        handleCheckPublicTariffForOrigin(origin);
      }
    });
  }, [
    handleCheckPublicTariffForOrigin,
    originsMissingRailFreightForDisplay,
    publicTariffStatusByOriginKey,
    requestedRailRateByOriginKey,
  ]);

  const handleRequestSingleRailOrigin = async (
    railOrigin: MissingRailFreightSectionItem,
  ) => {
    if (loadingRailRateRequestByOriginKey[railOrigin.originKey]) return;

    if (!railOrigin.competitor) {
      openSingleRailRateRequest(railOrigin);
      return;
    }

    setLoadingRailRateRequestByOriginKey((prev) => ({
      ...prev,
      [railOrigin.originKey]: true,
    }));
    try {
      await postCreateRailRequests([
        {
          originId: railOrigin.originId,
          originName: railOrigin.originName,
        },
      ]);
      await postRequestForRailRate([railOrigin]);
      markRailRateRequested([railOrigin.originKey]);
      savedNotification("Rail rate request submitted");
    } catch (error) {
      failedNotification(
        "Failed to submit rail rate request",
        error instanceof Error ? error.message : "Unknown error",
      );
    } finally {
      setLoadingRailRateRequestByOriginKey((prev) => ({
        ...prev,
        [railOrigin.originKey]: false,
      }));
    }
  };

  if (!shouldShowTruckMissing && !shouldShowRailMissing) {
    return null;
  }

  return (
    <div
      id="missing-origins"
      className="missing-origins-container multi-loc-quote-missing-origin"
    >
      <div className="inbound-modes-title">Missing Origins</div>
      <div id="missing-freight-section-id" className="missing-freight-section">
        <div id="missing-freight-content-id">
          <div id="fml-toggle-track" className="fml-toggle-track">
            {shouldShowTruckMissing && shouldShowRailMissing ? (
              <>
                <button
                  id="truck-missing-toggle-button"
                  type="button"
                  className={`fml-toggle-pill ${
                    missingFreightTab === "TRUCK" ? "active" : ""
                  }`}
                  onClick={() => setMissingFreightTab("TRUCK")}
                >
                  <TruckIconFO
                    color={
                      missingFreightTab === "TRUCK" ? "#1D4ED8" : "#6B7280"
                    }
                  />
                  <span
                    className={`fml-toggle-label ${
                      missingFreightTab === "TRUCK"
                        ? "fml-toggle-label-truck-active"
                        : "fml-toggle-label-inactive"
                    }`}
                  >
                    Truck
                  </span>
                </button>

                <button
                  type="button"
                  id="rail-missing-toggle-button"
                  className={`fml-toggle-pill ${
                    missingFreightTab === "RAIL" ? "active" : ""
                  }`}
                  onClick={() => setMissingFreightTab("RAIL")}
                >
                  <RailIconFO
                    color={missingFreightTab === "RAIL" ? "#F97316" : "#6B7280"}
                  />
                  <span
                    className={`fml-toggle-label ${
                      missingFreightTab === "RAIL"
                        ? "fml-toggle-label-rail-active"
                        : "fml-toggle-label-inactive"
                    }`}
                  >
                    Rail
                  </span>
                </button>
              </>
            ) : (
              <div className="fml-toggle-pill active fml-toggle-pill-single">
                {shouldShowTruckMissing ? (
                  <>
                    <TruckIconFO color="#1D4ED8" />
                    <span className="fml-toggle-label fml-toggle-label-truck-active">
                      Truck
                    </span>
                  </>
                ) : (
                  <>
                    <RailIconFO color="#F97316" />
                    <span className="fml-toggle-label fml-toggle-label-rail-active">
                      Rail
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {originsMissingProductForActiveTab.length > 0 && (
            <div id="origins-missing-product">
              <div
                id="origins-missing-product-title"
                className="origins-needing-setup-title"
              >
                Origins Missing Product
              </div>
              {originsMissingProductForActiveTab.map((quote) => {
                const missingProductName = formatProductName(
                  tariffCheckParams.selectedProduct ||
                    tariffCheckParams.product ||
                    "AMMONIA",
                );
                const favoriteOriginType = getFavoriteOriginType(quote);
                const addProductKey = `${
                  quote.origin.id || ""
                }:${favoriteOriginType}:${missingProductName.toUpperCase()}`;
                const quoteKey = `${quote.competitor ? "competitor" : "koch"}-${
                  quote.origin.id || quote.origin.name
                }-TRUCK`;
                const showProductLoadingStyle =
                  !!loadingAddProductByKey[addProductKey];
                const addMissingProductIcon = showProductLoadingStyle ? (
                  <ThreeDots color="#64748B" height={14} width={20} />
                ) : (
                  <img
                    id="add-missing-product-icon"
                    className="truck-setup-action-plus"
                    src={icons.AddIcon}
                    alt="Add Product"
                  />
                );
                const addMissingProductText = showProductLoadingStyle
                  ? "Adding..."
                  : `Add ${missingProductName.toUpperCase()}`;

                return (
                  <div
                    id="truck-add-product-item"
                    className="truck-setup-item truck-setup-item-inline-layout"
                    key={`setup-${quoteKey}`}
                  >
                    <div
                      id={`need-setup-${quoteKey}`}
                      className={`truck-setup-origin-name ${
                        quote.competitor
                          ? "truck-setup-origin-name-competitor"
                          : "truck-setup-origin-name-koch"
                      }`}
                    >
                      {formatOriginNameDisplay(quote.origin.name)}
                    </div>

                    <button
                      id="add-missing-product-button"
                      type="button"
                      className="truck-setup-action-btn"
                      onClick={() => onAddMissingProduct(quote)}
                      disabled={showProductLoadingStyle}
                    >
                      {addMissingProductIcon}
                      <span
                        id="add-missing-product-text"
                        className="truck-setup-action-text"
                      >
                        {addMissingProductText}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div id="need-guidance-content" className="setup-list-wrapper">
            {shouldShowTruckMissing &&
              missingFreightTab === "TRUCK" &&
              !loadingMissingMiles &&
              missingMilesOrigins.length > 0 && (
                <div
                  id="missing-miles-section"
                  className="missing-miles-section"
                >
                  <div className="origins-needing-setup-title">
                    Origins Missing Miles
                  </div>
                  {missingMilesOrigins.map((missingMilesOrigin) => {
                    const mileageOriginLoadingKey = `${
                      missingMilesOrigin.competitor ? "competitor" : "koch"
                    }-${missingMilesOrigin.originId}`;
                    const requestIsLoading =
                      !!loadingMileageRequestByOrigin[mileageOriginLoadingKey];
                    const requestSent =
                      !!missingMilesOrigin.validation?.mileageRequested;

                    let requestIcon = (
                      <span className="truck-setup-action-plus">
                        <img
                          id="add-missing-product-icon"
                          className="truck-setup-action-plus"
                          src={icons.AddIcon}
                          alt="Add Product"
                        />
                      </span>
                    );
                    let requestText = "Request";

                    if (requestIsLoading) {
                      requestIcon = (
                        <ThreeDots color="#64748B" height={14} width={20} />
                      );
                      requestText = "Requesting...";
                    } else if (requestSent) {
                      requestIcon = (
                        <img
                          className="truck-setup-success-icon"
                          src={icons.GreenCheckMark}
                          alt="Mileage Requested"
                        />
                      );
                      requestText = "Requested";
                    }

                    return (
                      <div
                        className="truck-setup-item missing-miles-row"
                        key={`missing-miles-${missingMilesOrigin.originId}`}
                      >
                        <div
                          id={`missing-miles-origin-name-${missingMilesOrigin.originId}`}
                          className={`truck-setup-origin-name ${
                            missingMilesOrigin.competitor
                              ? "truck-setup-origin-name-competitor"
                              : "truck-setup-origin-name-koch"
                          }`}
                        >
                          {formatOriginNameDisplay(
                            missingMilesOrigin.originName,
                          )}
                        </div>

                        <div
                          id={`missing-miles-actions-${missingMilesOrigin.originId}`}
                          className="truck-setup-actions"
                        >
                          <button
                            id="request-missing-miles-button"
                            type="button"
                            className={`truck-setup-action-btn ${
                              requestSent
                                ? "truck-setup-action-btn-success"
                                : ""
                            }`}
                            onClick={() =>
                              handleMileageRequest(missingMilesOrigin)
                            }
                            disabled={requestIsLoading || requestSent}
                          >
                            {requestIcon}
                            <span
                              className={`truck-setup-action-text ${
                                requestSent
                                  ? "truck-setup-action-text-success"
                                  : ""
                              }`}
                            >
                              {requestText}
                            </span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            {shouldShowTruckMissing &&
              missingFreightTab === "TRUCK" &&
              originsWithNoGuidance.length > 0 && (
                <div
                  id="origins-with-no-guidance-section"
                  className="outside-milage-band-section"
                >
                  <div className="origins-needing-setup-title">
                    Origins With No Guidance
                  </div>

                  {originsWithNoGuidance.map((outsideOrigin) => (
                    <div
                      className="truck-setup-item outside-mileage-row"
                      key={`outside-mileage-${outsideOrigin.originKey}`}
                    >
                      <div
                        id={`outside-mileage-origin-name-${
                          outsideOrigin.originId || outsideOrigin.originKey
                        }`}
                        className={`truck-setup-origin-name ${
                          outsideOrigin.competitor
                            ? "truck-setup-origin-name-competitor"
                            : "truck-setup-origin-name-koch"
                        }`}
                      >
                        {formatOriginNameDisplay(outsideOrigin.originName)}
                      </div>

                      <div
                        id={`no-guidance-description-${
                          outsideOrigin.originId || outsideOrigin.originKey
                        }`}
                        className={`outside-mileage-chip ${
                          (outsideOrigin.description || "").startsWith(
                            "Outside Mileage Band -",
                          )
                            ? "outside-mileage-chip-wrap"
                            : ""
                        }`}
                      >
                        {outsideOrigin.description}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {shouldShowRailMissing &&
              missingFreightTab === "RAIL" &&
              originsMissingRailFreightForDisplay.length > 0 && (
                <RequestTariffSection
                  originsMissingRailFreight={
                    originsMissingRailFreightForDisplay
                  }
                  requestedRailRateByOriginKey={requestedRailRateByOriginKey}
                  failedRailRateRequestByOriginKey={
                    failedRailRateRequestByOriginKey
                  }
                  loadingRailRateRequestByOriginKey={
                    loadingRailRateRequestByOriginKey
                  }
                  publicTariffStatusByOriginKey={publicTariffStatusByOriginKey}
                  publicTariffRateByOriginKey={publicTariffRateByOriginKey}
                  onRequestMultiple={onRequestMultipleRailRates}
                  onRequestSingleOrigin={handleRequestSingleRailOrigin}
                  onCheckPublicTariff={handleCheckPublicTariffForOrigin}
                />
              )}
          </div>
        </div>
      </div>

      {shouldShowTruckMissing &&
        missingFreightTab === "TRUCK" &&
        loadingMissingMiles && (
          <div className="missing-freight-loading-overlay">
            <LoadingIcon height={48} width={48} color="#60A5FA" />
          </div>
        )}

      {shouldShowRailMissing &&
        missingFreightTab === "RAIL" &&
        loadingRailMissingFreight && (
          <div className="missing-freight-loading-overlay">
            <LoadingIcon height={48} width={48} color="#60A5FA" />
          </div>
        )}
    </div>
  );
};

export default MultiLocQuoteMissingOrigin;
